'use client';
import { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useDispatch } from 'react-redux';
import { setAuthTokens } from '@/lib/slices/authSlice';
import { setTokenCookie } from '@/lib/utils/jwt';

export default function TokenHandler() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const dispatch = useDispatch();
  const [isProcessingToken, setIsProcessingToken] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');

    if (token && !isProcessingToken) {
      setIsProcessingToken(true);

      // Save token as access token with JWT expiration (same as regular login)
      setTokenCookie('accessToken', token);

      // For social login, set empty refreshToken - the function now handles empty tokens
      setTokenCookie('refreshToken', token);

      // Update Redux store
      dispatch(setAuthTokens({
        accessToken: token,
        refreshToken: token
      }));

      const url = new URL(window.location.href);
      url.searchParams.delete('token');
      window.history.replaceState({}, '', url.toString());

      // Show loader for smooth transition, then redirect
      // Use a verification approach to ensure cookies are properly set
      const verifyCookiesAndRedirect = () => {
        // Check if cookies are actually set in the browser
        const cookieAccessToken = document.cookie
          .split('; ')
          .find(row => row.startsWith('accessToken='));
        
        if (cookieAccessToken) {
          setIsProcessingToken(false);
          window.location.href = '/dashboard';
        } else {
          // Retry after a short delay if cookies aren't ready yet
          setTimeout(verifyCookiesAndRedirect, 200);
        }
      };

      // Start verification after initial delay
      setTimeout(verifyCookiesAndRedirect, 1000);
    }
  }, [searchParams, dispatch, router, isProcessingToken]);

  // Show loader when processing token
  if (isProcessingToken) {
    return (
      <div className="fixed inset-0 bg-skin z-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm mx-4 text-center">
          {/* App Logo */}
          <div className="mb-6">
            <img
              src="/assets/icons/mainlogo.svg"
              alt="Logo"
              className="h-12 w-auto mx-auto"
            />
          </div>

          {/* Animated Loader */}
          <div className="mb-6">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 mx-auto"></div>
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent absolute top-0 left-1/2 transform -translate-x-1/2"></div>
            </div>
          </div>

          {/* Success Icon Animation */}
          <div className="mb-4">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-3">
              <img
                src="/assets/icons/greenTick.svg"
                alt="Success"
                className="h-6 w-6"
              />
            </div>
          </div>

          {/* Text Content */}
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-gray-900">
              Authentication Successful!
            </h3>
            <p className="text-gray-600">
              Welcome back! Redirecting to your dashboard...
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null; // This component doesn't render anything when not processing
}